import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Users, Crown, Sparkles, TrendingUp, MessageSquare, 
  Calendar, Loader2, Search, ChevronLeft, ChevronRight,
  Mail, User, Shield, Trash2, AlertTriangle, Clock,
  Download, CheckSquare, Square, ArrowUpCircle, ListChecks, Edit, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [tierFilter, setTierFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [deleteModal, setDeleteModal] = useState({ open: false, member: null, bulk: false });
  const [editModal, setEditModal] = useState({ open: false, member: null });
  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const [activeTab, setActiveTab] = useState('members');
  const [recentUpgrades, setRecentUpgrades] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [blockAndDelete, setBlockAndDelete] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    checkAdminAccess();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
      fetchMembers();
      fetchRecentUpgrades();
      fetchWaitlist();
      fetchBlockedUsers();
    }
  }, [isAdmin, tierFilter, page]);

  const checkAdminAccess = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/check`, {
        headers: getAuthHeader()
      });
      setIsAdmin(response.data.is_admin);
      if (!response.data.is_admin) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Admin check failed:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/members`, {
        headers: getAuthHeader(),
        params: { skip: page * pageSize, limit: pageSize, tier: tierFilter }
      });
      setMembers(response.data);
      setSelectedMembers(new Set());
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const fetchRecentUpgrades = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/recent-upgrades`, {
        headers: getAuthHeader(),
        params: { days: 30 }
      });
      setRecentUpgrades(response.data);
    } catch (error) {
      console.error('Failed to fetch upgrades:', error);
    }
  };

  const fetchWaitlist = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/waitlist`, {
        headers: getAuthHeader()
      });
      setWaitlist(response.data);
    } catch (error) {
      console.error('Failed to fetch waitlist:', error);
    }
  };

  const fetchBlockedUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/blocked-users`, {
        headers: getAuthHeader()
      });
      setBlockedUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch blocked users:', error);
    }
  };

  const deleteMember = async (memberId, shouldBlock = false) => {
    setDeleting(true);
    try {
      if (shouldBlock) {
        await axios.post(`${API_URL}/admin/delete-and-block/${memberId}`, {
          reason: 'Deleted and blocked by admin'
        }, {
          headers: getAuthHeader()
        });
        toast.success('Member deleted and blocked from re-registering');
        fetchBlockedUsers();
      } else {
        await axios.delete(`${API_URL}/admin/members/${memberId}`, {
          headers: getAuthHeader()
        });
        toast.success('Member deleted successfully');
      }
      setDeleteModal({ open: false, member: null, bulk: false });
      setBlockAndDelete(false);
      fetchMembers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete member');
    } finally {
      setDeleting(false);
    }
  };

  const unblockUser = async (email) => {
    try {
      await axios.delete(`${API_URL}/admin/blocked-users/${encodeURIComponent(email)}`, {
        headers: getAuthHeader()
      });
      toast.success('User unblocked');
      fetchBlockedUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unblock user');
    }
  };

  const bulkDeleteMembers = async () => {
    setDeleting(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/members/bulk-delete`,
        { user_ids: Array.from(selectedMembers) },
        { headers: getAuthHeader() }
      );
      toast.success(`Deleted ${response.data.deleted_count} members`);
      if (response.data.skipped_admins > 0) {
        toast.info(`Skipped ${response.data.skipped_admins} admin accounts`);
      }
      setDeleteModal({ open: false, member: null, bulk: false });
      setSelectedMembers(new Set());
      fetchMembers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete members');
    } finally {
      setDeleting(false);
    }
  };

  const exportMembers = () => {
    window.open(`${API_URL}/admin/export/members`, '_blank');
  };

  const exportWaitlist = () => {
    window.open(`${API_URL}/admin/export/waitlist`, '_blank');
  };

  const deleteWaitlistSignup = async (signupId) => {
    if (!window.confirm('Are you sure you want to remove this person from the waitlist?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/admin/waitlist/${signupId}`, {
        headers: getAuthHeader()
      });
      toast.success('Removed from waitlist');
      fetchWaitlist();
    } catch (error) {
      toast.error('Failed to delete waitlist signup');
    }
  };

  const updateMember = async (memberId, updateData) => {
    setUpdating(true);
    try {
      await axios.put(`${API_URL}/admin/members/${memberId}`, updateData, {
        headers: getAuthHeader()
      });
      toast.success('Member updated successfully');
      setEditModal({ open: false, member: null });
      fetchMembers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    } finally {
      setUpdating(false);
    }
  };

  const toggleSelectMember = (memberId) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMembers(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedMembers.size === filteredMembers.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(filteredMembers.map(m => m.id)));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLastActiveText = (lastActive) => {
    if (!lastActive) return 'Never';
    const date = new Date(lastActive);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const isInactive = (lastActive) => {
    if (!lastActive) return true;
    const date = new Date(lastActive);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    return diffDays > 90;
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'vip':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white">VIP</span>;
      case 'founding':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white">Founding</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-stone-200 text-stone-600">Free</span>;
    }
  };

  const filteredMembers = members.filter(member => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      member.email?.toLowerCase().includes(query) ||
      member.name?.toLowerCase().includes(query) ||
      member.first_name?.toLowerCase().includes(query) ||
      member.last_name?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-stone-900">Admin Dashboard</h1>
          <p className="text-stone-600">Overview of your community and membership</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card-cosmic p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-stone-600">Total Members</span>
            </div>
            <p className="text-3xl font-bold text-stone-900">{stats.members.total}</p>
          </div>

          <div className="card-cosmic p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm font-medium text-stone-600">Founding Members</span>
            </div>
            <p className="text-3xl font-bold text-stone-900">{stats.members.founding}</p>
          </div>

          <div className="card-cosmic p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Crown className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-sm font-medium text-stone-600">VIP Members</span>
            </div>
            <p className="text-3xl font-bold text-stone-900">{stats.members.vip}</p>
          </div>

          <div className="card-cosmic p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm font-medium text-stone-600">New (30 days)</span>
            </div>
            <p className="text-3xl font-bold text-stone-900">{stats.activity.recent_signups}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-stone-200 overflow-x-auto">
        <button
          onClick={() => setActiveTab('members')}
          className={`px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
            activeTab === 'members' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-stone-600 hover:text-stone-900'
          }`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          All Members
        </button>
        <button
          onClick={() => setActiveTab('upgrades')}
          className={`px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
            activeTab === 'upgrades' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-stone-600 hover:text-stone-900'
          }`}
        >
          <ArrowUpCircle className="w-4 h-4 inline mr-2" />
          Recent Upgrades
          {recentUpgrades.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
              {recentUpgrades.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('waitlist')}
          className={`px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
            activeTab === 'waitlist' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-stone-600 hover:text-stone-900'
          }`}
        >
          <ListChecks className="w-4 h-4 inline mr-2" />
          Retreat Waitlist
          {waitlist.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
              {waitlist.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('blocked')}
          className={`px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
            activeTab === 'blocked' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-stone-600 hover:text-stone-900'
          }`}
        >
          <Shield className="w-4 h-4 inline mr-2" />
          Blocked Users
          {blockedUsers.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">
              {blockedUsers.length}
            </span>
          )}
        </button>
      </div>

      {/* Members Tab */}
      {activeTab === 'members' && (
        <div className="card-cosmic p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-stone-900">Members</h2>
              {selectedMembers.size > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteModal({ open: true, member: null, bulk: true })}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected ({selectedMembers.size})
                </Button>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" size="sm" onClick={exportMembers}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full sm:w-64"
                />
              </div>
              
              <select
                value={tierFilter}
                onChange={(e) => { setTierFilter(e.target.value); setPage(0); }}
                className="px-3 py-2 rounded-lg border border-stone-200 bg-white text-stone-700"
              >
                <option value="all">All Tiers</option>
                <option value="free">Free</option>
                <option value="founding">Founding</option>
                <option value="vip">VIP</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left py-3 px-4">
                    <button onClick={toggleSelectAll} className="text-stone-400 hover:text-stone-600">
                      {selectedMembers.size === filteredMembers.length && filteredMembers.length > 0 ? (
                        <CheckSquare className="w-5 h-5" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Member</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Tier</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Last Active</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Joined</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-stone-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((member) => (
                  <tr key={member.id} className={`border-b border-stone-100 hover:bg-stone-50 ${isInactive(member.last_active) ? 'bg-red-50/50' : ''}`}>
                    <td className="py-3 px-4">
                      <button 
                        onClick={() => toggleSelectMember(member.id)}
                        className="text-stone-400 hover:text-stone-600"
                      >
                        {selectedMembers.has(member.id) ? (
                          <CheckSquare className="w-5 h-5 text-primary" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                      </button>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center overflow-hidden">
                          {member.avatar_url ? (
                            <img 
                              src={`${process.env.REACT_APP_BACKEND_URL}${member.avatar_url}`}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-sm font-semibold text-primary">
                              {member.first_name?.charAt(0) || member.name?.charAt(0) || '?'}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-stone-900">
                            {member.first_name && member.last_name 
                              ? `${member.first_name} ${member.last_name}`
                              : member.name || 'Unknown'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 text-stone-600">
                        <Mail className="w-4 h-4 text-stone-400" />
                        {member.email}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {getTierBadge(member.membership_tier)}
                    </td>
                    <td className="py-3 px-4">
                      <div className={`flex items-center gap-1 ${isInactive(member.last_active) ? 'text-red-600' : 'text-stone-600'}`}>
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{getLastActiveText(member.last_active)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-stone-600">
                      {formatDate(member.created_at)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditModal({ open: true, member })}
                          className="text-primary hover:bg-primary/10"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteModal({ open: true, member, bulk: false })}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-stone-200">
            <p className="text-sm text-stone-600">
              Showing {page * pageSize + 1} - {page * pageSize + filteredMembers.length} members
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={members.length < pageSize}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Recent Upgrades Tab */}
      {activeTab === 'upgrades' && (
        <div className="card-cosmic p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-stone-900">Recent Upgrades (Last 30 Days)</h2>
          </div>

          {recentUpgrades.length === 0 ? (
            <div className="text-center py-12">
              <ArrowUpCircle className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-600">No upgrades in the last 30 days</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Member</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Upgraded To</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Amount</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentUpgrades.map((upgrade, idx) => (
                    <tr key={idx} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-stone-900">
                          {upgrade.user.first_name} {upgrade.user.last_name || upgrade.user.name}
                        </p>
                        <p className="text-sm text-stone-500">{upgrade.user.email}</p>
                      </td>
                      <td className="py-3 px-4">
                        {getTierBadge(upgrade.transaction.tier)}
                      </td>
                      <td className="py-3 px-4 font-medium text-stone-900">
                        ${upgrade.transaction.amount}
                      </td>
                      <td className="py-3 px-4 text-stone-600">
                        {formatDateTime(upgrade.transaction.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Waitlist Tab */}
      {activeTab === 'waitlist' && (
        <div className="card-cosmic p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-stone-900">Retreat Waitlist</h2>
            {waitlist.length > 0 && (
              <Button variant="outline" size="sm" onClick={exportWaitlist}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            )}
          </div>

          {waitlist.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-600">No waitlist signups yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Signed Up</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-stone-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {waitlist.map((signup, idx) => (
                    <tr key={signup.id || idx} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-3 px-4 font-medium text-stone-900">
                        {signup.name || 'N/A'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 text-stone-600">
                          <Mail className="w-4 h-4 text-stone-400" />
                          {signup.email}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-stone-600">
                        {formatDateTime(signup.created_at)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteWaitlistSignup(signup.id)}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Blocked Users Tab */}
      {activeTab === 'blocked' && (
        <div className="card-cosmic p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-stone-900">Blocked Users</h2>
            <p className="text-sm text-stone-500">Users blocked from re-registering</p>
          </div>

          {blockedUsers.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No blocked users</p>
              <p className="text-sm mt-2">Users you delete and block will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Reason</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Blocked On</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-stone-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blockedUsers.map((blocked, idx) => (
                    <tr key={blocked.id || idx} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 text-stone-900">
                          <Mail className="w-4 h-4 text-stone-400" />
                          {blocked.email}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-stone-600">
                        {blocked.reason || 'No reason provided'}
                      </td>
                      <td className="py-3 px-4 text-stone-600">
                        {formatDateTime(blocked.blocked_at)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => unblockUser(blocked.email)}
                          className="text-green-600 hover:bg-green-50 hover:text-green-700"
                        >
                          Unblock
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-cosmic p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-stone-900">
                  {deleteModal.bulk ? `Delete ${selectedMembers.size} Members?` : 'Delete Member?'}
                </h3>
                <p className="text-sm text-stone-500">This action cannot be undone</p>
              </div>
            </div>
            
            {deleteModal.bulk ? (
              <p className="text-stone-600 mb-6">
                Are you sure you want to delete <strong>{selectedMembers.size} members</strong>? 
                This will permanently delete their accounts, profile data, and all activity.
                Admin accounts will be skipped.
              </p>
            ) : (
              <>
                <p className="text-stone-600 mb-2">
                  Are you sure you want to delete this member?
                </p>
                <div className="bg-stone-100 rounded-lg p-3 mb-4">
                  <p className="font-medium text-stone-900">
                    {deleteModal.member?.first_name} {deleteModal.member?.last_name || deleteModal.member?.name}
                  </p>
                  <p className="text-sm text-stone-600">{deleteModal.member?.email}</p>
                </div>
                <label className="flex items-center gap-2 mb-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={blockAndDelete}
                    onChange={(e) => setBlockAndDelete(e.target.checked)}
                    className="w-4 h-4 rounded border-stone-300 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-stone-700">Also block from re-registering (for bots/spammers)</span>
                </label>
              </>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setDeleteModal({ open: false, member: null, bulk: false });
                  setBlockAndDelete(false);
                }}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={() => deleteModal.bulk ? bulkDeleteMembers() : deleteMember(deleteModal.member.id, blockAndDelete)}
                disabled={deleting}
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  deleteModal.bulk ? 'Delete All' : (blockAndDelete ? 'Delete & Block' : 'Delete Member')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {editModal.open && editModal.member && (
        <EditMemberModal
          member={editModal.member}
          onClose={() => setEditModal({ open: false, member: null })}
          onSave={updateMember}
          updating={updating}
        />
      )}
    </div>
  );
};

// Edit Member Modal Component
const EditMemberModal = ({ member, onClose, onSave, updating }) => {
  const [membershipTier, setMembershipTier] = useState(member.membership_tier || 'free');
  const [hasAlignmentLab, setHasAlignmentLab] = useState(member.has_alignment_lab || false);
  const [isAlumni, setIsAlumni] = useState(member.is_alignment_lab_alumni || false);

  const handleSave = () => {
    onSave(member.id, {
      membership_tier: membershipTier,
      has_alignment_lab: hasAlignmentLab,
      is_alignment_lab_alumni: isAlumni
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="card-cosmic p-6 max-w-lg w-full">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Edit className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-stone-900">Edit Member</h3>
              <p className="text-sm text-stone-500">{member.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Member Info */}
        <div className="bg-stone-100 rounded-lg p-4 mb-6">
          <p className="font-medium text-stone-900">
            {member.first_name} {member.last_name || member.name || ''}
          </p>
          <p className="text-sm text-stone-600">{member.email}</p>
        </div>

        {/* Membership Tier */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-stone-700 mb-2">
            Membership Tier
          </label>
          <select
            value={membershipTier}
            onChange={(e) => setMembershipTier(e.target.value)}
            className="w-full border border-stone-300 rounded-lg px-4 py-3 text-stone-900 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="free">Free</option>
            <option value="founding">Founding Member ($11/mo)</option>
            <option value="vip">Premium Access / VIP ($75/mo)</option>
          </select>
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-800">
              <strong>Note:</strong> Changes here only affect app access. Any billing changes (subscriptions, cancellations, refunds) must also be made manually in your <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">Stripe Dashboard</a>.
            </p>
          </div>
        </div>

        {/* Alignment Lab Access */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-stone-700 mb-3">
            30-Day Alignment Lab
          </label>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hasAlignmentLab}
                onChange={(e) => setHasAlignmentLab(e.target.checked)}
                className="w-5 h-5 rounded border-stone-300 text-primary focus:ring-primary"
              />
              <span className="text-stone-700">Has Alignment Lab Access</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isAlumni}
                onChange={(e) => setIsAlumni(e.target.checked)}
                className="w-5 h-5 rounded border-stone-300 text-primary focus:ring-primary"
              />
              <span className="text-stone-700">Is Alignment Lab Alumni</span>
            </label>
          </div>
          <p className="text-xs text-stone-500 mt-2">
            Alumni have completed the 30-day program and have lifetime access to all content.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={updating}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 btn-cosmic"
            onClick={handleSave}
            disabled={updating}
          >
            {updating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
